/**
 * listLettermintRoutes — RPC contract for the read-only Lettermint route
 * discovery callable (firebase/src/settings/listLettermintRoutes.api.ts).
 * Lettermint has no account-wide route listing (routes are reachable only
 * per-project, GET /projects then GET /projects/{id}/routes), so this walks
 * both levels server-side and hands the admin panel's route picker a flat,
 * human-readable list — the operator picks a route by NAME, never an opaque
 * id, and the picked `id` is what a follow-up `saveIntegration` call for
 * `id: "lettermint"` supplies as its own `routeId` field.
 *
 * No input: the callable resolves the already-saved Lettermint team token
 * itself (workspaces/{orgId}/integrations/lettermint, Admin SDK only) —
 * there is nothing else to discover routes with, and a not-yet-saved
 * credential has no routes to discover against either.
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export const ListLettermintRoutesInput = z.object({ orgId: Id }).strict();
export type ListLettermintRoutesInput = z.infer<
	typeof ListLettermintRoutesInput
>;

// Fields justified — the operator's own picker needs enough to choose
// correctly without ever having to paste or guess Lettermint's own opaque
// route id:
//   id          — the value a follow-up save's `routeId` field supplies.
//   name        — the route's own operator-given name — what the picker
//                 actually renders; Lettermint's own dashboard shows no
//                 route id anywhere either.
//   projectName — which project the route belongs to, since Lettermint
//                 scopes routes under projects and a team account often
//                 holds several.
//   routeType   — Lettermint's own route classification (e.g.
//                 "transactional"), surfaced so an operator can tell a
//                 transactional route from a marketing/bulk one at a glance.
//   isDefault   — whether this is the project's default route.
//
// Every value but `isDefault` is Lettermint's own, reaching this schema
// through the handler's projection — so the `.min(1)` floors and `.strict()`
// bind different things. The floors bind provider data, and hold because
// Lettermint's create validation makes `name` required on both a project and
// a route, refusing the empty string as well as a missing one: a blank name
// cannot be stored there to be read back here. Over a bare `z.string()` the
// floor adds exactly one refusal, `""`, since a row missing the field
// outright is already refused by the type itself. `.strict()` binds only the
// seam between this deployment's own server and its own client: the
// projection maps five explicit keys with no spread, so a field Lettermint
// adds later never reaches this schema at all.
export const LettermintRouteSchema = z
	.object({
		id: z.string().min(1),
		name: z.string().min(1),
		projectName: z.string().min(1),
		routeType: z.string().min(1),
		isDefault: z.boolean(),
	})
	.strict();
export type LettermintRoute = z.infer<typeof LettermintRouteSchema>;

export const ListLettermintRoutesOutput = z
	.object({
		routes: z.array(LettermintRouteSchema),
	})
	.strict();
export type ListLettermintRoutesOutput = z.infer<
	typeof ListLettermintRoutesOutput
>;
